import React from 'react';
import { ChevronDown, X, Briefcase, FileText, Calculator, Layers, Euro, Building } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import { quantityStep, sanitizeQuantity } from 'core/utils/units';

const ServiceItem = ({
  service,
  index,
  isExpanded,
  onToggleExpansion,
  onRemove,
  onUpdate,
  units,
  themeColors,
  kontierungstyp
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  return (
    <div className="relative group/service">
      {/* Service Card Background */}
      <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-800/20 border border-white/10' : 'bg-gray-100/40 border border-gray-300/20'} backdrop-blur-xl rounded-2xl`}></div>
      
      <div className="relative p-4">
        <div className="flex justify-between items-start mb-4">
          <button
            onClick={() => onToggleExpansion(service.id)}
            className="flex items-center space-x-3 group/serviceheader hover:opacity-80 transition-opacity duration-200 flex-1"
          >
            <h4 className={`text-md font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'} text-left`}>
              {service.service_description || t('banf.service.defaultName', { index: index + 1 })}
            </h4>
            <div className={`${isDarkMode ? 'text-white/60' : 'text-gray-500'} transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
              <ChevronDown className="w-4 h-4" />
            </div>
          </button>
          <button
            onClick={() => onRemove(service.id)}
            className="text-red-500 hover:text-red-600 p-1 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors ml-2"
            title={t('banf.service.remove')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Collapsible Service Content */}
        <div className={`transition-all duration-500 ease-in-out ${isExpanded ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Leistungsnummer */}
            <div>
              <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90' : 'text-gray-700'} mb-2 flex items-center drop-shadow-sm`}>
                <Briefcase className="w-4 h-4 mr-1.5" />
                {t('banf.service.serviceNumber')}
              </label>
              <div className="relative">
                <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-800/30 border border-white/30' : 'bg-white/50 border border-blue-200/40'} backdrop-blur-xl rounded-2xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]`}></div>
                <input
                  type="text"
                  value={service.service_number}
                  onChange={(e) => onUpdate(service.id, 'service_number', e.target.value)}
                  className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:ring-2 ${themeColors.focusRing} focus:outline-none transition-all duration-300 ${isDarkMode ? 'text-white placeholder-white/60' : 'text-gray-800 placeholder-gray-400'}`}
                  placeholder={t('banf.service.serviceNumberPlaceholder')}
                />
              </div>
            </div>

            {/* Kurztext */}
            <div>
              <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90' : 'text-gray-700'} mb-2 flex items-center drop-shadow-sm`}>
                <FileText className="w-4 h-4 mr-1.5" />
                {t('banf.service.shortText')}
              </label>
              <div className="relative">
                <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-800/30 border border-white/30' : 'bg-white/50 border border-blue-200/40'} backdrop-blur-xl rounded-2xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]`}></div>
                <input
                  type="text"
                  value={service.service_description}
                  onChange={(e) => onUpdate(service.id, 'service_description', e.target.value)}
                  className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:ring-2 ${themeColors.focusRing} focus:outline-none transition-all duration-300 ${isDarkMode ? 'text-white placeholder-white/60' : 'text-gray-800 placeholder-gray-400'}`}
                  placeholder={t('banf.service.shortTextPlaceholder')}
                />
              </div>
            </div>

            {/* Menge */}
            <div>
              <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90' : 'text-gray-700'} mb-2 flex items-center drop-shadow-sm`}>
                <Calculator className="w-4 h-4 mr-1.5" />
                {t('banf.service.quantity')}
              </label>
              <div className="relative">
                <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-800/30 border border-white/30' : 'bg-white/50 border border-blue-200/40'} backdrop-blur-xl rounded-2xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]`}></div>
                <input
                  type="number"
                  min={quantityStep(service.service_unit)}
                  step={quantityStep(service.service_unit)}
                  value={service.service_quantity}
                  onChange={(e) => onUpdate(service.id, 'service_quantity', sanitizeQuantity(e.target.value, service.service_unit))}
                  className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:ring-2 ${themeColors.focusRing} focus:outline-none transition-all duration-300 ${isDarkMode ? 'text-white placeholder-white/60' : 'text-gray-800 placeholder-gray-400'} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                />
              </div>
            </div>

            {/* Mengeneinheit */}
            <div>
              <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90' : 'text-gray-700'} mb-2 flex items-center drop-shadow-sm`}>
                <Layers className="w-4 h-4 mr-1.5" />
                {t('banf.service.unitOfMeasure')}
              </label>
              <div className="relative">
                <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-800/30 border border-white/30' : 'bg-white/50 border border-blue-200/40'} backdrop-blur-xl rounded-2xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]`}></div>
                <select
                  value={service.service_unit}
                  onChange={(e) => onUpdate(service.id, 'service_unit', e.target.value)}
                  className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:ring-2 ${themeColors.focusRing} focus:outline-none transition-all duration-300 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}
                >
                  {units.map(unit => (
                    <option key={unit} value={unit} className={isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-800"}>{unit}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Bruttopreis */}
            <div>
              <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90' : 'text-gray-700'} mb-2 flex items-center drop-shadow-sm`}>
                <Euro className="w-4 h-4 mr-1.5" />
                {t('banf.service.grossPrice')}
              </label>
              <div className="relative">
                <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-800/30 border border-white/30' : 'bg-white/50 border border-blue-200/40'} backdrop-blur-xl rounded-2xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]`}></div>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={service.service_gross_price}
                  onChange={(e) => onUpdate(service.id, 'service_gross_price', e.target.value)}
                  className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:ring-2 ${themeColors.focusRing} focus:outline-none transition-all duration-300 ${isDarkMode ? 'text-white placeholder-white/60' : 'text-gray-800 placeholder-gray-400'} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                  placeholder="0.00"
                />
              </div>
            </div>

            {/* Währung */}
            <div>
              <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90' : 'text-gray-700'} mb-2 flex items-center drop-shadow-sm`}>
                <Euro className="w-4 h-4 mr-1.5" />
                {t('banf.service.currency')}
              </label>
              <div className="relative">
                <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-800/30 border border-white/30' : 'bg-white/50 border border-blue-200/40'} backdrop-blur-xl rounded-2xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]`}></div>
                <select
                  value={service.service_currency}
                  onChange={(e) => onUpdate(service.id, 'service_currency', e.target.value)}
                  className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:ring-2 ${themeColors.focusRing} focus:outline-none transition-all duration-300 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}
                >
                  <option value="EUR" className={isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-800"}>EUR</option>
                  <option value="USD" className={isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-800"}>USD</option>
                  <option value="CHF" className={isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-800"}>CHF</option>
                  <option value="GBP" className={isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-800"}>GBP</option>
                </select>
              </div>
            </div>

            {/* Kostenstelle - immer anzeigen */}
            <div>
              <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90' : 'text-gray-700'} mb-2 flex items-center drop-shadow-sm`}>
                <Building className="w-4 h-4 mr-1.5" />
                {t('banf.service.costCenter')}
              </label>
              <div className="relative">
                <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-800/30 border border-white/30' : 'bg-white/50 border border-blue-200/40'} backdrop-blur-xl rounded-2xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]`}></div>
                <input
                  type="text"
                  value={service.service_cost_center}
                  onChange={(e) => onUpdate(service.id, 'service_cost_center', e.target.value)}
                  className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:ring-2 ${themeColors.focusRing} focus:outline-none transition-all duration-300 ${isDarkMode ? 'text-white placeholder-white/60' : 'text-gray-800 placeholder-gray-400'}`}
                  placeholder={t('banf.service.costCenterPlaceholder')}
                />
              </div>
            </div>

            {/* Sachkonto - nur bei Auftrag-Typ anzeigen */}
            {kontierungstyp === 'auftrag' && (
              <div>
                <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90' : 'text-gray-700'} mb-2 flex items-center drop-shadow-sm`}>
                  <Calculator className="w-4 h-4 mr-1.5" />
                  {t('banf.service.glAccount')}
                </label>
                <div className="relative">
                  <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-800/30 border border-white/30' : 'bg-white/50 border border-blue-200/40'} backdrop-blur-xl rounded-2xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]`}></div>
                  <input
                    type="text"
                    value={service.service_gl_account || ''}
                    onChange={(e) => onUpdate(service.id, 'service_gl_account', e.target.value)}
                    className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:ring-2 ${themeColors.focusRing} focus:outline-none transition-all duration-300 ${isDarkMode ? 'text-white placeholder-white/60' : 'text-gray-800 placeholder-gray-400'}`}
                    placeholder={t('banf.service.glAccountPlaceholder')}
                  />
                </div>
              </div>
            )}

            {/* Auftrag - nur wenn kontierungstyp auftrag ist */}
            {kontierungstyp === 'auftrag' && (
              <div>
                <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90' : 'text-gray-700'} mb-2 flex items-center drop-shadow-sm`}>
                  <FileText className="w-4 h-4 mr-1.5" />
                  {t('banf.service.order')}
                </label>
                <div className="relative">
                  <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-800/30 border border-white/30' : 'bg-white/50 border border-blue-200/40'} backdrop-blur-xl rounded-2xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]`}></div>
                  <input
                    type="text"
                    value={service.service_auftrag}
                    onChange={(e) => onUpdate(service.id, 'service_auftrag', e.target.value)}
                    className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:ring-2 ${themeColors.focusRing} focus:outline-none transition-all duration-300 ${isDarkMode ? 'text-white placeholder-white/60' : 'text-gray-800 placeholder-gray-400'}`}
                    placeholder={t('banf.service.orderPlaceholder')}
                    required
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceItem;